import update from "immutability-helper";
import { LineItemType } from "legacyComponents/LineItemContainer.types";

import { cn } from "@btnet/utilities-core";

import { BTSelectItem } from "types/apiResponse/apiResponse";
import { ProposalGroupTypes } from "types/enum";

import { getDefaultMarkupMarginForCostType } from "utilities/lineItem/lineItemHelper";
import {
    getMarkupPercentageFromMarginPercentage,
    recalculateAllFields,
} from "utilities/lineItem/markupUtility";
import { isNullOrUndefined } from "utilities/object/object";

import { RelatedItemType } from "commonComponents/entity/relatedItem/RelatedItem.types";
import { GridLineItemColumnType } from "commonComponents/utilities/LineItemContainer/types/LineItem.enums";
import {
    type IGridLineItemActionRow,
    type ILineItemColumn,
} from "commonComponents/utilities/LineItemContainer/types/LineItem.interfaces";
import {
    GroupingType,
    isLineItemActionRow,
    isLineItemRow,
} from "commonComponents/utilities/LineItemContainer/types/LineItem.types";
import { type WorksheetLineItemContainerRow } from "commonComponents/utilities/ProposalBaseLineItemContainer/ProposalBaseLineItemContainer.types";

import { MarkupColumnTypes } from "handlers";

import {
    type FormatItem,
    type FormatItemList,
    type IFormatItemPathIndices,
    InScopeOptionModes,
    isFormatItemListGrouped,
    isProposalCategory,
    isUnassignedCategory,
    LineItemsToGet,
    OptionMode,
    type ProposalCategory,
    type ProposalSubCategory,
    UnassignedCostGroupId,
    type WorksheetLineItem,
} from "entity/estimate/common/estimate.common.types";
import { type ProposalGroupForDisplay } from "entity/estimate/common/FormattedProposal/FormattedProposal.types";
import { unassignedGroupId } from "entity/estimate/common/LineItemQuickFind/LineItemQuickFind.types";
import {
    BTSelectItem_ParentGroupClass,
    BTSelectItem_SubgroupClass,
} from "entity/estimate/common/MoveLineItems/MoveLineItems";
import { type IProposalFormatData } from "entity/estimate/common/ProposalFormat/ProposalFormat.types";
import { getSubCategories } from "entity/estimate/common/ProposalFormat/ProposalFormat.utility";
import { type ICostTypesMarkup } from "entity/job/Job.api.types";
import { NoTaxId } from "entity/tax/TaxRate/TaxRate.api.types";

export function getFormatLineItemsFromFormatItemList(
    formatItemList: readonly WorksheetLineItemContainerRow[],
): WorksheetLineItem[] {
    const resultList: WorksheetLineItem[] = [];
    for (const formatItem of formatItemList) {
        if (isProposalLineItemGroupRow(formatItem)) {
            // handle parent group
            for (const subFormatItem of formatItem.items) {
                if (isProposalLineItemGroupRow(subFormatItem)) {
                    // handle subgroup
                    resultList.push(...subFormatItem.items);
                } else {
                    resultList.push(subFormatItem);
                }
            }
        } else if (isProposalLineItemRow(formatItem)) {
            resultList.push(formatItem);
        }
    }
    return resultList;
}

export function getFilteredFormatData(
    formatItemList: FormatItemList,
    lineItemsToGet: LineItemsToGet,
    favoritedIds?: number[],
): FormatItemList {
    if (lineItemsToGet === LineItemsToGet.All) {
        return formatItemList;
    }

    switch (lineItemsToGet) {
        case LineItemsToGet.InScope:
            return formatItemList.filter(
                (item) =>
                    !isProposalLineItemGroupRow(item) ||
                    InScopeOptionModes.includes(item.optionMode),
            );
        case LineItemsToGet.OutOfScope:
            return formatItemList.filter(
                (item) =>
                    isProposalLineItemGroupRow(item) &&
                    !InScopeOptionModes.includes(item.optionMode),
            );
        case LineItemsToGet.OnlyRequired:
            return formatItemList.filter(
                (item) =>
                    !isProposalLineItemGroupRow(item) || item.optionMode === OptionMode.Required,
            );
        case LineItemsToGet.OnlyPending:
            return formatItemList.filter(
                (item) =>
                    isProposalLineItemGroupRow(item) && item.optionMode === OptionMode.Pending,
            );
        case LineItemsToGet.OnlyApproved:
            return formatItemList.filter(
                (item) =>
                    isProposalLineItemGroupRow(item) && item.optionMode === OptionMode.Approved,
            );
        case LineItemsToGet.OnlyDeclined:
            return formatItemList.filter(
                (item) =>
                    isProposalLineItemGroupRow(item) && item.optionMode === OptionMode.Declined,
            );
        case LineItemsToGet.OnlyFavorited:
            if (!favoritedIds || favoritedIds.length === 0) {
                return [];
            }
            return formatItemList.filter(
                (item) =>
                    isProposalLineItemGroupRow(item) &&
                    item.optionMode === OptionMode.Pending &&
                    favoritedIds.includes(item.id),
            );
        case LineItemsToGet.OnlyDecided:
            return formatItemList.filter(
                (item) =>
                    isProposalLineItemGroupRow(item) &&
                    (item.optionMode === OptionMode.Approved ||
                        item.optionMode === OptionMode.Declined),
            );
    }
}

export function getFormatGroupsFromFormatItems(
    formatItemList: WorksheetLineItemContainerRow[],
    includeSubGroups: boolean,
    onlyDirtyItems: boolean = false,
): ProposalCategory[] {
    const formatGroups: ProposalCategory[] = [];

    for (const item of formatItemList) {
        if (isProposalLineItemGroupRow(item)) {
            if (!onlyDirtyItems || item._isDirty) {
                formatGroups.push(item);
            }
            if (includeSubGroups) {
                formatGroups.push(
                    ...getFormatGroupsFromFormatItems(item.items, false, onlyDirtyItems),
                );
            }
        }
    }

    return formatGroups;
}

export function getBTSelectItemFormatGroupsFromFormatItems(
    formatItems: FormatItem[],
    decidedOptionItems: Record<number, OptionMode>,
): BTSelectItem[] {
    // Filter out categories without titles
    const categories = formatItems.filter(
        (fi): fi is ProposalCategory => isProposalCategory(fi) && fi.title.trim() !== "",
    );

    const categoriesAndSubCategories = categories.map((d) => [d, ...getSubCategories(d)]).flat();

    const categoriesThatCanBeSelected: BTSelectItem[] = categoriesAndSubCategories.map((x) => {
        if (isProposalSubCategory(x)) {
            return new BTSelectItem({
                id: x.id,
                title: x.title,
                className: BTSelectItem_SubgroupClass,
                disabled:
                    decidedOptionItems[x.id] === OptionMode.Approved ||
                    decidedOptionItems[x.id] === OptionMode.Declined,
            });
        }
        return new BTSelectItem({
            id: x.id,
            title: x.title,
            className: BTSelectItem_ParentGroupClass,
            disabled:
                decidedOptionItems[x.id] === OptionMode.Approved ||
                decidedOptionItems[x.id] === OptionMode.Declined,
        });
    });

    // Currently, it has been decided we will always leave the unassigned group up.
    // If unassigned group isn't visible, make it available to move to as a select item
    const unassignedCategory = categories.find(isUnassignedCategory);
    if (!unassignedCategory) {
        categoriesThatCanBeSelected.push(
            new BTSelectItem({
                id: UnassignedCostGroupId,
                title: "Unassigned",
                className: BTSelectItem_ParentGroupClass,
            }),
        );
    }

    return categoriesThatCanBeSelected;
}

export function flattenNestedFormatData<T extends WorksheetLineItemContainerRow>(
    formatItemList: T[],
): (T | ProposalSubCategory | WorksheetLineItem)[] {
    return formatItemList.flatMap<T | ProposalSubCategory | WorksheetLineItem>((item) => {
        return isProposalLineItemGroupRow(item)
            ? [
                  item,
                  ...item.items.flatMap((subItem) =>
                      isProposalLineItemGroupRow(subItem) ? [subItem, ...subItem.items] : [subItem],
                  ),
              ]
            : [item];
    });
}

export function findItemInEdit(
    data: WorksheetLineItemContainerRow[],
): WorksheetLineItemContainerRow | undefined {
    return flattenNestedFormatData(data).find(
        (item) => isNotProposalLineItemActionRow(item) && item._isInEdit,
    );
}

export const childStylePlaceholderProperty: string = "childStylePlaceholder";

/**
 * @param row group row
 * @param collapsedIds list of group ids (stored in local storage) that are collapsed
 * @returns whether the row is in the list of collapsed groups
 */
export const isExpanded = (row: ProposalCategory | WorksheetLineItem, collapsedIds: number[]) => {
    if (isProposalLineItemGroupRow(row) && row.jobId) {
        if (flattenNestedFormatData([row]).some((item) => item.id !== row.id && item._isInEdit)) {
            return true;
        }
        return !collapsedIds.includes(
            row.id === unassignedGroupId ? getUniqueGroupIdForUnassigned(row.jobId) : row.id,
        );
    } else {
        return row.isExpanded;
    }
};

export const isFavorited = (
    group: ProposalGroupForDisplay,
    jobId: number,
    favoritedIds?: number[],
) => {
    return !!favoritedIds?.includes(
        group.proposalFormatItemId === unassignedGroupId
            ? getUniqueGroupIdForUnassigned(jobId)
            : group.proposalFormatItemId,
    );
};

/**
 * @returns the unique id that identifies that row across leads/jobs
 *
 * ⚠ Warning, we already use negative ids for newly created groups and line items and shouldn't be
 * reusing them here. Realistically this should never collide or cause a significant problem even
 * if it does, so we will leave it as is for now.
 */
export const getUniqueGroupIdForUnassigned = (jobId?: number, leadId?: number) => {
    // If the row is the unassigned row (id === -1),
    // then use the negative lead/job id so that the id is unique across leads/jobs.
    if (leadId) {
        return -leadId;
    } else if (jobId) {
        return -jobId;
    } else {
        // Default back to the -1 id if somehow neither of those fields are populated.
        return unassignedGroupId;
    }
};

export const getEstimateRowKey = (row: WorksheetLineItemContainerRow) => {
    if (isProposalLineItemActionRow(row)) {
        return `actionrow-${row.id}`;
    } else if (isProposalLineItemGroupRow(row)) {
        if (row.id === unassignedGroupId) {
            return `unassigned-${getUniqueGroupIdForUnassigned(row.jobId, row.leadId)}}`;
        }
        return `group-${row.id}`;
    } else {
        return `lineitem-${row.id}`;
    }
};

export function transformNestedFormatDataForDisplay<T extends WorksheetLineItemContainerRow>(
    formatItemList: T[],
    collapsedIds: number[],
): (T | ProposalSubCategory | WorksheetLineItem)[] {
    /** This method adds the childStylePlaceholder property so that antd will style these rows properly.
     * Without these array placeholder properties, antd thinks these elements don't have children
     * and modifies the first few columns on the table.
     */
    return formatItemList.flatMap<T | ProposalSubCategory | WorksheetLineItem>((item) => {
        if (isProposalLineItemGroupRow(item) && isExpanded(item, collapsedIds)) {
            return [
                { ...item, [childStylePlaceholderProperty]: [] },
                ...item.items.flatMap((subItem) =>
                    isProposalLineItemGroupRow(subItem) && isExpanded(subItem, collapsedIds)
                        ? [{ ...subItem, [childStylePlaceholderProperty]: [] }, ...subItem.items]
                        : [{ ...subItem, [childStylePlaceholderProperty]: [] }],
                ),
            ];
        } else if (isProposalLineItemGroupRow(item)) {
            return [{ ...item, [childStylePlaceholderProperty]: [] }];
        } else {
            return [item];
        }
    });
}

export function getParentFormatItemOfFormatItem(
    row: WorksheetLineItemContainerRow,
    data: WorksheetLineItemContainerRow[],
): ProposalCategory | null {
    for (const dataItem of data) {
        if (isProposalLineItemGroupRow(dataItem)) {
            for (const subItem of dataItem.items) {
                if (subItem.id === row.id) {
                    return dataItem;
                }
                if (isProposalLineItemGroupRow(subItem)) {
                    for (const subGroupLineItem of subItem.items) {
                        if (subGroupLineItem.id === row.id) {
                            return subItem;
                        }
                    }
                }
            }
        }
    }
    return null;
}

/**
 * @param id id of the format item
 * @param categories proposal categories passed in
 * @returns matching item if found, otherwise null
 */
export function getFormatItemInCategoriesById(
    id: string | number,
    categories: WorksheetLineItemContainerRow[],
): FormatItem | null {
    for (const category of categories) {
        if (!isProposalLineItemGroupRow(category)) {
            continue;
        }

        if (category.id === id) {
            return category;
        }

        const item = getFormatItemInCategoryById(id, category);
        if (item) {
            return item;
        }
    }
    return null;
}

export function findCategoryById(
    groupId: number,
    formatItems: FormatItemList,
): ProposalCategory | undefined {
    if (isFormatItemListGrouped(formatItems)) {
        const item = getFormatItemInCategoriesById(groupId, formatItems);
        if (item && isProposalLineItemGroupRow(item)) {
            return item;
        }
    }
    return undefined;
}

/**
 * Persists a proposal format category. Groups only exist in the ProposalWorksheet
 * view and can't be saved from revised costs, so the save no-ops (returns []) when
 * either gate fails. Otherwise it dispatches on whether the category already exists:
 * existing groups (id > 0) go through the batch format update, new groups (id <= 0)
 * go through the create endpoint. Shared by the inline group edit
 * (WorksheetPresentational) and the AG Grid split-view group detail panel
 * (Worksheet) so both the gating and the create-vs-update decision live in one place.
 */
export async function saveProposalFormatCategory(
    category: ProposalCategory,
    gate: { isRevisedCosts: boolean; estimateGroupType: ProposalGroupTypes },
    handlers: {
        onUpdateFormatItems: (categories: ProposalCategory[]) => Promise<ProposalCategory[]>;
        onCreateFormatCategory?: (category: ProposalCategory) => Promise<ProposalCategory[]>;
        onEditFormatCategory?: (category: ProposalCategory) => Promise<ProposalCategory[]>;
    },
): Promise<ProposalCategory[]> {
    if (gate.isRevisedCosts || gate.estimateGroupType !== ProposalGroupTypes.ProposalWorksheet) {
        return [];
    }
    if (category.id > 0) {
        // Change orders persist group content through a dedicated handler; estimates fall back
        // to updating the group via onUpdateFormatItems.
        if (handlers.onEditFormatCategory) {
            return await handlers.onEditFormatCategory(category);
        }
        return await handlers.onUpdateFormatItems([category]);
    }
    return (await handlers.onCreateFormatCategory?.(category)) ?? [];
}

/**
 * The subset of worksheet state that {@link WorksheetActionStateBuilder} reads to
 * decide whether an add/edit action is disabled and, if so, what tooltip to show.
 * Kept intentionally narrow (rather than the full sidebar prop bag) so both the
 * sidebar and the AG Grid split button can supply it without dragging along unused
 * props. `currentViewType` / `formatGroupType` mirror the sidebar's
 * `proposalViewDropdownProps.value` / `proposalFormatGroupDropdownProps.value`.
 */
export interface IWorksheetActionState {
    isWorksheetLocked: boolean;
    isRevisedCosts: boolean;
    hasLineItemsSelected: boolean;
    hasLineItemInEdit: boolean;
    clientPriceLocked?: boolean;
    currentViewType?: ProposalGroupTypes;
    formatGroupType?: ProposalGroupTypes;
    disabledActionMessage?: string;
}

/**
 * Builds the `[isDisabled, tooltipText]` pair for a worksheet add/edit action from
 * the current {@link IWorksheetActionState}. Chain the `with*` guards that apply to
 * an action, then call `build(label)`; the first matching guard wins and returns its
 * tooltip, otherwise the action is enabled and the tooltip is just the label.
 *
 * Shared by the vertical/horizontal sidebar (WorksheetSidebar) and the AG Grid
 * split button (Worksheet) so both surfaces disable the same actions for the same
 * reasons and show identical tooltip copy. The builder is stateful across a single
 * `build` call and resets its guards afterward, so one instance can produce every
 * action's state sequentially.
 */
export class WorksheetActionStateBuilder {
    constructor(state: IWorksheetActionState) {
        this.state = state;
    }

    state: IWorksheetActionState;

    reset = () => {
        this.ifDisabledForLocked = false;
        this.ifDisabledForCostCategory = false;
        this.ifDisabledForNone = false;
        this.ifDisabledForTakeoff = false;
        this.ifDisabledForGroups = false;
        this.ifUseProposalWorksheetOnly = false;
        this.ifSelectItems = false;
        this.selectItemsLabel = undefined;
        this.ifDeselectItems = false;
        this.ifDisableDuringLineItemEdit = false;
        this.ifDisabledForCostCodeGrouping = false;
        this.ifDisableIfClientPriceLocked = false;
    };

    // Returns [isDisabled, popoverText]
    build = (label: string): [boolean, string] => {
        const [isDisabled, popoverText] = this.buildInternal(label);
        this.reset();
        return [isDisabled, popoverText];
    };

    private buildInternal = (label: string): [boolean, string] => {
        const {
            isWorksheetLocked,
            hasLineItemsSelected,
            hasLineItemInEdit,
            currentViewType,
            formatGroupType,
            isRevisedCosts,
            disabledActionMessage,
        } = this.state;

        if (this.ifDisableIfClientPriceLocked && this.state.clientPriceLocked) {
            return [true, "Disabled while pending, approved, or declined - recall to enable"];
        }

        if (this.ifDisabledForLocked && (isWorksheetLocked || isRevisedCosts)) {
            const lockedMessage = disabledActionMessage
                ? `${disabledActionMessage}`
                : `${label} disabled when locked`;
            return [true, lockedMessage];
        }

        if (this.ifDisabledForGroups && currentViewType === ProposalGroupTypes.Assembly) {
            const groupsLabel = "groups";
            return [true, `${label} disabled in ${groupsLabel}`];
        }

        if (this.ifDisabledForCostCategory && currentViewType === ProposalGroupTypes.CostCategory) {
            return [true, `${label} disabled in cost code`];
        }

        if (
            this.ifDisabledForCostCodeGrouping &&
            currentViewType === ProposalGroupTypes.ProposalWorksheet &&
            formatGroupType === ProposalGroupTypes.CostCategory
        ) {
            return [true, `${label} disabled in cost code grouping`];
        }

        if (this.ifDisabledForNone && currentViewType === ProposalGroupTypes.None) {
            return [true, `${label} disabled in list`];
        }

        if (this.ifDisabledForTakeoff && currentViewType === ProposalGroupTypes.Takeoff) {
            return [true, `${label} disabled in Takeoff Assemblies`];
        }

        if (this.ifSelectItems && !hasLineItemsSelected) {
            const selectItemsLabel = this.selectItemsLabel ?? label;
            return [true, `Select items to ${selectItemsLabel}`];
        }

        if (this.ifDisableDuringLineItemEdit && hasLineItemInEdit) {
            return [true, "Finish editing line item to enable"];
        }

        if (this.ifDeselectItems && hasLineItemsSelected) {
            return [true, "Deselect items to enable"];
        }

        return [false, label];
    };

    ifDisabledForLocked?: boolean = false;
    withDisabledForLocked = () => {
        this.ifDisabledForLocked = true;
        return this;
    };

    ifDisabledForCostCategory?: boolean = false;
    withDisabledForCostCategory = () => {
        this.ifDisabledForCostCategory = true;
        return this;
    };

    ifDisabledForNone?: boolean = false;
    withDisabledForNone = () => {
        this.ifDisabledForNone = true;
        return this;
    };

    ifDisabledForTakeoff?: boolean = false;
    withDisabledForTakeoff = () => {
        this.ifDisabledForTakeoff = true;
        return this;
    };

    ifDisabledForGroups?: boolean = false;
    withDisabledForGroups = () => {
        this.ifDisabledForGroups = true;
        return this;
    };

    ifUseProposalWorksheetOnly?: boolean = false;
    withUseProposalWorksheetOnly = () => {
        this.ifUseProposalWorksheetOnly = true;
        this.withDisabledForGroups()
            .withDisabledForCostCategory()
            .withDisabledForNone()
            .withDisabledForTakeoff()
            .withDisabledForLocked();
        return this;
    };

    ifSelectItems?: boolean = false;
    selectItemsLabel?: string;
    withSelectItems = (overrideLabel?: string) => {
        this.ifSelectItems = true;
        this.selectItemsLabel = overrideLabel;
        return this;
    };

    ifDeselectItems?: boolean = false;
    withDeselectItems = () => {
        this.ifDeselectItems = true;
        return this;
    };

    ifDisableDuringLineItemEdit?: boolean = false;
    withDisableDuringLineItemEdit() {
        this.ifDisableDuringLineItemEdit = true;
        return this;
    }

    ifDisabledForCostCodeGrouping?: boolean = false;
    withDisabledForCostCodeGrouping() {
        this.ifDisabledForCostCodeGrouping = true;
        return this;
    }

    ifDisableIfClientPriceLocked?: boolean = false;
    withDisableIfClientPriceLocked() {
        this.ifDisableIfClientPriceLocked = true;
        return this;
    }
}

export function computeGroupFieldPath(
    category: ProposalCategory,
    formatItems: FormatItemList,
    dataField: string = "formatItems",
): string {
    if (!isFormatItemListGrouped(formatItems)) return "";
    const indices = getFormatItemPathIndices(category.id, formatItems);
    if (!indices) return "";
    if (indices.subCategoryIndex !== undefined && indices.subCategoryIndex !== null) {
        return `${dataField}[${indices.rootCategoryIndex}].items[${indices.subCategoryIndex}]`;
    }
    return `${dataField}[${indices.rootCategoryIndex}]`;
}

/**
 * @param id id of the format item
 * @param category proposal category
 * @returns matching item if found, otherwise null
 */
function getFormatItemInCategoryById(
    id: string | number,
    category: WorksheetLineItemContainerRow,
): FormatItem | null {
    if (!isProposalLineItemGroupRow(category)) {
        return null;
    }

    for (const item of category.items) {
        if (item.id === id) {
            return item;
        }

        const subItem = getFormatItemInSubCategoryById(id, item);
        if (subItem) {
            return subItem;
        }
    }
    return null;
}

/**
 * @param id id of the format item
 * @param subCategory sub proposal category
 * @returns matching item if found, otherwise null
 */
function getFormatItemInSubCategoryById(
    id: string | number,
    subCategory: WorksheetLineItemContainerRow,
): FormatItem | null {
    if (!isProposalLineItemGroupRow(subCategory)) {
        return null;
    }

    return getFormatItemFromListById(id, subCategory.items) ?? null;
}

/**
 * @param id id of the format item
 * @param items format items
 * @returns matching item if found, otherwise null
 */
function getFormatItemFromListById(id: string | number, items: FormatItem[]): FormatItem | null {
    for (const item of items) {
        if (item.id === id) {
            return item;
        }
    }
    return null;
}

export function selectEverythingUnderParentCategory(
    row: WorksheetLineItemContainerRow,
    data: WorksheetLineItemContainerRow[],
    selected: boolean,
    decidedOptionItems: Record<number, OptionMode>,
    getDataItemFieldPath: (
        dataItem: WorksheetLineItemContainerRow,
        data: WorksheetLineItemContainerRow[],
    ) => string,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
) {
    if (decidedOptionItems[Number(row.id)] === OptionMode.Declined) {
        return;
    }

    if (!isProposalLineItemGroupRow(row)) {
        const path = getDataItemFieldPath(row, data);
        const updatedRow = { ...row, isSelected: selected };
        setFieldValue(path, updatedRow, false);
        return;
    }

    if (!isProposalCategory(row)) {
        return;
    }

    const updatedCategory = {
        ...row,
        isSelected: selected,
        items: row.items.map((rowItem) => {
            if (isProposalSubCategory(rowItem)) {
                return {
                    ...rowItem,
                    isSelected: selected,
                    items: rowItem.items.map((x) => {
                        return { ...x, isSelected: selected };
                    }),
                };
            }
            return { ...rowItem, isSelected: selected };
        }),
    };

    const categoryPath = getDataItemFieldPath(row, data);
    setFieldValue(categoryPath, updatedCategory, false);
}

export function selectEverythingInFormatItemList(
    data: WorksheetLineItemContainerRow[],
    selected: boolean,
    decidedOptionItems: Record<number, OptionMode>,
) {
    const newSelectedList = [];

    for (const dataItem of data) {
        if (decidedOptionItems[Number(dataItem.id)] === OptionMode.Declined) {
            // Don't allow declined option items to be selected
            newSelectedList.push(dataItem);
            continue;
        }

        if (isProposalLineItemGroupRow(dataItem)) {
            const newDataItem = {
                ...dataItem,
                isSelected: selected,
                items: dataItem.items.map((rowItem) => {
                    if (isProposalSubCategory(rowItem)) {
                        return {
                            ...rowItem,
                            isSelected: selected,
                            items: rowItem.items.map((x) => {
                                return { ...x, isSelected: selected };
                            }),
                        };
                    }
                    return { ...rowItem, isSelected: selected };
                }),
            };
            newSelectedList.push(newDataItem);
        } else {
            const updatedRow = { ...dataItem, isSelected: selected };
            newSelectedList.push(updatedRow);
        }
    }

    return newSelectedList;
}

/**
 * Bulk select-all / deselect-all helper. Filters out action rows from the format
 * list (they aren't selectable) and applies `isSelected` uniformly to every
 * line item / group via `selectEverythingInFormatItemList`. Use as the new
 * `formatItems` value passed to Formik's `setFieldValue`.
 */
export function setIsSelectedOnAllItems(
    formatItems: WorksheetLineItemContainerRow[],
    isSelected: boolean,
    decidedOptionItems: Record<number, OptionMode>,
) {
    return selectEverythingInFormatItemList(
        formatItems.filter((item) => !isLineItemActionRow(item)),
        isSelected,
        decidedOptionItems,
    );
}

export function isProposalLineItemRow(
    item: WorksheetLineItemContainerRow,
): item is WorksheetLineItem {
    return (item as WorksheetLineItem).costCodeId !== undefined;
}

export function isProposalLineItemGroupRow(
    item: WorksheetLineItemContainerRow,
): item is ProposalCategory {
    return (item as ProposalCategory).items !== undefined;
}

export function isProposalLineItemActionRow(
    item: WorksheetLineItemContainerRow,
): item is IGridLineItemActionRow {
    return (item as IGridLineItemActionRow).isActionRow;
}

export function isNotProposalLineItemActionRow(
    item: WorksheetLineItemContainerRow,
): item is Exclude<WorksheetLineItemContainerRow, IGridLineItemActionRow> {
    return !isProposalLineItemActionRow(item);
}

export const isProposalSubCategory = (
    formatItem: WorksheetLineItemContainerRow,
): formatItem is ProposalSubCategory => {
    return (
        isNotProposalLineItemActionRow(formatItem) &&
        isProposalCategory(formatItem) &&
        !isNullOrUndefined(formatItem.parentId) &&
        formatItem.parentId !== UnassignedCostGroupId
    );
};

export function getBTSelectItemParentGroupsFromFormatItems(
    formatItems: FormatItem[],
    decidedOptionItems: Record<number, OptionMode>,
    excludeId?: number,
): BTSelectItem[] {
    return formatItems
        .filter(
            (fi): fi is ProposalCategory =>
                isProposalCategory(fi) &&
                !isProposalSubCategory(fi) &&
                !isUnassignedCategory(fi) &&
                fi.title.trim() !== "" &&
                fi.id !== excludeId,
        )
        .map(
            (x) =>
                new BTSelectItem({
                    id: x.id,
                    title: x.title,
                    className: BTSelectItem_ParentGroupClass,
                    disabled:
                        decidedOptionItems[x.id] === OptionMode.Approved ||
                        decidedOptionItems[x.id] === OptionMode.Declined,
                }),
        );
}

export function toRootCategory(subCategory: ProposalSubCategory): ProposalCategory {
    return {
        ...subCategory,
        name: "Category",
        parentId: undefined,
    };
}

/**
 * Throws away child groups. We currently only support one level of group nesting.
 */
export function toSubCategory(category: ProposalCategory, parentId: number): ProposalSubCategory {
    return {
        ...category,
        name: "Sub Category",
        parentId,
        items: category.items.filter(isLineItemRow),
    };
}

export function findMostRecentRowIndex(data: WorksheetLineItemContainerRow[]): number | undefined {
    const newestAddedInEditIndex = data.findIndex(
        (row) => isNotProposalLineItemActionRow(row) && row._isInEdit,
    );

    if (newestAddedInEditIndex >= 0) {
        return newestAddedInEditIndex;
    } else {
        return undefined;
    }
}

export function filterOutLineItemsFromFormatItemList(
    formatData: FormatItemList,
    lineItemIds: number[],
    leaveEmptyCategories: boolean,
): [ProposalCategory[], number[]] {
    const updatedFormatData: ProposalCategory[] = [];
    const filteredOutFormatItemGroupIds: number[] = [];

    for (const parentFormatItem of formatData) {
        if (!isProposalCategory(parentFormatItem)) {
            continue;
        }
        filterOutLineItems(
            parentFormatItem,
            new Set(lineItemIds),
            filteredOutFormatItemGroupIds,
            updatedFormatData,
            leaveEmptyCategories,
        );
    }
    return [updatedFormatData, filteredOutFormatItemGroupIds];
}

/**
 * Figures out which line items rows and groups should be filtered out of PFIs as deleted after a set of line items are deleted.
 * @param formatItem - The format item to filter items from
 * @param setOfLineItemIdsToFilterOut - The IDs of recently deleted line items
 * @param filteredOutFormatItemGroupIds - OUTPUT - This list contains IDs of all format item group that should be removed
 * @param updatedProposalCategories - OUTPUT - This is an updated state value for proposal categories.
 * @param leaveEmptyCategories - If true, any empty categories will be left in the proposal format, which means filteredOutFormatItemGroupIds will be empty.
 * @returns Remaining sub items (line items or subgroups). This is primarily for recursive use of this function.
 */
function filterOutLineItems(
    formatItem: ProposalCategory,
    setOfLineItemIdsToFilterOut: Set<number>,
    filteredOutFormatItemGroupIds: number[],
    updatedProposalCategories: ProposalCategory[],
    leaveEmptyCategories: boolean = false,
) {
    const subItems: (WorksheetLineItem | ProposalSubCategory)[] = [];
    for (const subItem of formatItem.items) {
        if (isProposalSubCategory(subItem)) {
            // recursively filter deleted line items on subgroup
            const internalSubItems = filterOutLineItems(
                subItem,
                setOfLineItemIdsToFilterOut,
                filteredOutFormatItemGroupIds,
                updatedProposalCategories,
                leaveEmptyCategories,
            );
            // if subgroup still has line items, include it in parent
            if (internalSubItems.length > 0 || leaveEmptyCategories) {
                subItems.push(
                    update(subItem, {
                        items: {
                            $set: getFormatLineItemsFromFormatItemList(internalSubItems),
                        },
                    }),
                );
            }
        } else if (!setOfLineItemIdsToFilterOut.has(subItem.id)) {
            subItems.push(subItem);
        }
    }
    // Check that we actually deleted items
    const hasDeletedItems = formatItem.items.length !== subItems.length;
    if (subItems.length === 0 && hasDeletedItems && !leaveEmptyCategories) {
        // If no items remain in the parent format item, keep track of it so we can
        // let the server know it's deleted
        // The only exception is if the id of the group to delete is
        // <= UnassignedCostGroupId, as that means the group being deleted hasn't
        // even been saved yet.
        if (formatItem.id > UnassignedCostGroupId) {
            filteredOutFormatItemGroupIds.push(formatItem.id);
        }
        // Now don't bother adding the deleted format item to the updated array
    } else if (!isProposalSubCategory(formatItem)) {
        updatedProposalCategories.push(
            update(formatItem, {
                items: {
                    $set: subItems,
                },
            }),
        );
    }
    return subItems;
}

export function isProposalLineItemRowInSubCategory(
    row: WorksheetLineItemContainerRow,
    data: WorksheetLineItemContainerRow[],
): boolean {
    if (!isProposalLineItemRow(row)) {
        return false;
    }

    const rowParentCategory = getParentFormatItemOfFormatItem(row, data);
    if (!rowParentCategory) {
        return false;
    }
    return isProposalSubCategory(rowParentCategory);
}

/**
 * Returns the path indices for the format item.
 */
export function getFormatItemPathIndices(
    itemId: number | string,
    rootCategories: ProposalCategory[],
): IFormatItemPathIndices | undefined {
    if (rootCategories.length === 0) {
        return;
    }

    for (const [rootCategoryIndex, rootCategory] of rootCategories.entries()) {
        // Check to see if the item is the root category
        if (itemId === rootCategories[rootCategoryIndex].id) {
            return { id: itemId, rootCategoryIndex };
        }

        if (rootCategory.items.length === 0) {
            continue;
        }

        // Check to see if the item is in this root category
        for (const [rootItemIndex, rootItem] of rootCategory.items.entries()) {
            if (rootItem.id === itemId) {
                return {
                    id: itemId,
                    rootCategoryIndex: rootCategoryIndex,
                    subCategoryIndex: isProposalSubCategory(rootItem) ? rootItemIndex : undefined,
                    lineItemIndex: isProposalLineItemRow(rootItem) ? rootItemIndex : undefined,
                };
            }
            // Subcategory check
            if (isProposalSubCategory(rootItem) && rootItem.items.length !== 0) {
                // Check to see if the line item is in any of the subcategories
                for (const [subItemIndex, subItem] of rootItem.items.entries()) {
                    // Line Item Check
                    if (subItem.id === itemId && isProposalLineItemRow(subItem)) {
                        return {
                            id: itemId,
                            rootCategoryIndex: rootCategoryIndex,
                            subCategoryIndex: rootItemIndex,
                            lineItemIndex: subItemIndex,
                        };
                    }
                }
            }
        }
    }
    // The Unassigned Category will have to be created
    if (itemId === UnassignedCostGroupId) {
        return { id: UnassignedCostGroupId, rootCategoryIndex: -1 };
    }

    return undefined;
}

export const canClickIntoWorksheetCell = (
    record: WorksheetLineItemContainerRow,
    colType: GridLineItemColumnType,
    groupingType?: GroupingType,
    isEditable: boolean = true,
) => {
    if (colType === GridLineItemColumnType.action) {
        return false;
    }

    if (isProposalLineItemGroupRow(record)) {
        return groupingType !== GroupingType.None && isEditable;
    }

    // Revised costs is never editable, so no worries about change orders here.
    const isChangeOrderLineItem =
        isProposalLineItemRow(record) && record.lineItemType === LineItemType.ChangeOrder;
    const isTitleAndCostCodeColumn =
        colType === GridLineItemColumnType.worksheetTitleAndCostCode ||
        colType === GridLineItemColumnType.titleAndCostCode;
    const isClickableTitleAndCostCode =
        (isChangeOrderLineItem && isTitleAndCostCodeColumn) || !isTitleAndCostCodeColumn;

    return (
        isEditable &&
        isClickableTitleAndCostCode &&
        isProposalLineItemRow(record) &&
        record.isEditable
    );
};

export const getClassNamesForProposalTableCell = (
    colIndex: number,
    record: WorksheetLineItemContainerRow,
    groupingType?: GroupingType,
) => {
    if (isProposalLineItemActionRow(record)) {
        return "text-left";
    } else if (isProposalCategory(record)) {
        return cn({
            "text-left": colIndex === 0,
            proposalFormatCategoryTitleCell: groupingType !== GroupingType.None && colIndex === 0,
            editing: record._isInEdit,
        });
    } else {
        return cn({
            proposalFormatLineItemTitleCell: colIndex === 0,
            editing: record._isInEdit,
        });
    }
};

const getColSpanForProposalActionRowCell = (
    colIndex: number,
    columnsList: ILineItemColumn<WorksheetLineItem, unknown>[],
    isExpandable: boolean,
    hasSelectableRows: boolean,
) => {
    if (colIndex !== 0) {
        return 0;
    }

    let colSpanLength = columnsList.length;

    // need to account for the expand and select columns ant adds
    if (isExpandable) {
        colSpanLength++;
    }
    if (hasSelectableRows) {
        colSpanLength++;
    }

    return colSpanLength;
};

const getColSpanForProposalGroupRowCell = (
    colIndex: number,
    columnsList: ILineItemColumn<WorksheetLineItem, unknown>[],
    groupingType: GroupingType,
) => {
    // this shouldn't happen
    if (groupingType === GroupingType.None) {
        return 0;
    }

    const isTitleColumn = colIndex === 0;
    const isNextAfterTitleColumn = colIndex === 1;

    if (!isTitleColumn && !isNextAfterTitleColumn) {
        return 1;
    }

    // we will allow the title cell to span 2 columns if there is no content in the adjacent cell
    const nextColAfterTitle = columnsList[1];
    const colSpanForTitle =
        !nextColAfterTitle ||
        nextColAfterTitle.columnType === GridLineItemColumnType.action ||
        nextColAfterTitle.getGroupedValue
            ? 1
            : 2;

    if (isTitleColumn) {
        return colSpanForTitle;
    } else {
        // the next column needs to adjust for the title column to keep column count consistent
        return 2 - colSpanForTitle;
    }
};

interface IColSpanForTableCellParams {
    colIndex: number;
    columnsList: ILineItemColumn<WorksheetLineItem, unknown>[];
    record: WorksheetLineItemContainerRow;
    groupingType?: GroupingType;
    hasSelectableRows: boolean;
    isExpandable: boolean;
}

export const getColSpanForProposalTableCell = (params: IColSpanForTableCellParams) => {
    const {
        colIndex,
        columnsList,
        record,
        groupingType = GroupingType.GroupedLineItems,
        hasSelectableRows,
        isExpandable,
    } = params;

    if (isProposalLineItemActionRow(record)) {
        return getColSpanForProposalActionRowCell(
            colIndex,
            columnsList,
            isExpandable,
            hasSelectableRows,
        );
    } else if (isProposalCategory(record)) {
        return getColSpanForProposalGroupRowCell(colIndex, columnsList, groupingType);
    } else {
        return 1;
    }
};

export const isRowTiedToBidPackage = (lineItem: WorksheetLineItem) => {
    return lineItem.relatedItems?.find((x) => x.type === RelatedItemType.BidPackage) !== undefined;
};

export const hasEditPerms = (
    proposalData: IProposalFormatData,
    row: WorksheetLineItem,
    canEditGeneralItems: boolean,
    canAddGeneralItems: boolean,
) => {
    return (
        ((canEditGeneralItems && row.id > 0) || (canAddGeneralItems && row.id === 0)) &&
        !proposalData.worksheetLocked
    );
};

export const isRowEditable = (
    proposalData: IProposalFormatData,
    row: WorksheetLineItem,
    canEditGeneralItems: boolean,
    canAddGeneralItems: boolean,
) => {
    if (proposalData.isProposalWorksheet || proposalData.isRevisedCosts) return false;
    const hasEditPerm = hasEditPerms(proposalData, row, canEditGeneralItems, canAddGeneralItems);
    return (
        [LineItemType.EstimateLineItem, LineItemType.ChangeOrder].includes(row.lineItemType) &&
        !isRowTiedToBidPackage(row) &&
        isNullOrUndefined(row.takeoffLineItemId) &&
        hasEditPerm
    );
};

// Same as isRowEditable minus the bid package check
export const isUnitCostEditable = (
    proposalData: IProposalFormatData,
    row: WorksheetLineItem,
    canEditGeneralItems: boolean,
    canAddGeneralItems: boolean,
) => {
    if (proposalData.isProposalWorksheet || proposalData.isRevisedCosts) return false;
    return (
        [LineItemType.EstimateLineItem, LineItemType.ChangeOrder].includes(row.lineItemType) &&
        row.takeoffLineItemId == null &&
        hasEditPerms(proposalData, row, canEditGeneralItems, canAddGeneralItems)
    );
};

// Same as isRowEditable minus the takeoff check
export const isTaxEditable = (
    proposalData: IProposalFormatData,
    row: WorksheetLineItem,
    canEditGeneralItems: boolean,
    canAddGeneralItems: boolean,
    taxGroupId: number,
    isTaxOnCOEnabled: boolean = false,
) => {
    if (proposalData.isProposalWorksheet) return false;

    switch (row.lineItemType) {
        case LineItemType.EstimateLineItem:
            break;
        case LineItemType.ChangeOrder:
            if (!isTaxOnCOEnabled) return false;
            break;
        case LineItemType.Bid:
            break;
        default:
            return false;
    }

    return (
        hasEditPerms(proposalData, row, canEditGeneralItems, canAddGeneralItems) &&
        taxGroupId !== NoTaxId
    );
};

/**
 * Users can edit markup and margin on all line items except selection choices, declined, or approved options.
 */
export const isMarkupOrMarginEditable = (
    isProposalWorksheet: boolean,
    lineItem: WorksheetLineItem,
    decidedOptionItems: Record<number, OptionMode>,
) => {
    if (isProposalWorksheet) return false;

    const disallowedLineItemTypes: LineItemType[] = [
        LineItemType.SelectionChoice,
        LineItemType.Allowance,
        LineItemType.SelectionOption,
    ];
    return !(
        disallowedLineItemTypes.includes(lineItem.lineItemType) ||
        decidedOptionItems[lineItem.id] === OptionMode.Declined ||
        decidedOptionItems[lineItem.id] === OptionMode.Approved
    );
};

/**
 * While allowance items can have markup and margin added directly when first adding to the worksheet,
 * we do not allow profit to be applied to them through other means.
 */
export const canApplyMarkupOrMargin = (
    isProposalWorksheet: boolean,
    lineItem: WorksheetLineItem,
    decidedOptionItems: Record<number, OptionMode>,
) => {
    if (lineItem.lineItemType === LineItemType.Allowance) return false;
    return isMarkupOrMarginEditable(isProposalWorksheet, lineItem, decidedOptionItems);
};

/**
 * True when the line item is linked to a catalog cost item. The Apply-Profit API
 * silently skips these unless `shouldBreakLink` is true (the "Replace catalog
 * linked item profit targets" checkbox in the Profit and Tax modal). Matches the
 * truthiness check used by `LinkedCostItemIcon` — newly-created items can carry
 * `costItemId === 0` and must not be treated as linked.
 */
export const isCatalogLinked = (lineItem: WorksheetLineItem): boolean =>
    lineItem.costItemId != null && lineItem.costItemId !== 0;

export const isLineItemEditable = (
    lineItem: WorksheetLineItem,
    canEditGeneralItems: boolean,
    canAddGeneralItems: boolean,
    isWorksheetLocked: boolean,
) => {
    return (
        ((canEditGeneralItems && lineItem.id > 0) || (canAddGeneralItems && lineItem.id === 0)) &&
        !isWorksheetLocked
    );
};

export const getSelectedItemsFromFormatItems = (formatItems: FormatItemList) => {
    return isFormatItemListGrouped(formatItems)
        ? getFormatLineItemsFromFormatItemList(formatItems).filter((li) => li.isSelected)
        : formatItems.filter((li) => li.isSelected);
};

export const getUpdatedRowData = (
    data: WorksheetLineItemContainerRow[],
    rows: WorksheetLineItemContainerRow[],
): WorksheetLineItemContainerRow[] => {
    if (rows.length === 0) {
        return data;
    }
    const rowsMap: Map<string | number, WorksheetLineItemContainerRow> = new Map(
        rows.map((row) => [row.id, row]),
    );
    return data.map((item) => {
        if (rowsMap.has(item.id)) {
            return rowsMap.get(item.id) as WorksheetLineItemContainerRow;
        }
        if (isProposalLineItemGroupRow(item)) {
            return getUpdatedCategory(item, rowsMap) as WorksheetLineItemContainerRow;
        }
        return item;
    });
};

const getUpdatedCategory = (
    category: ProposalCategory,
    rowsMap: Map<string | number, WorksheetLineItemContainerRow>,
) => {
    return {
        ...category,
        items: category.items.map((item) => {
            if (rowsMap.has(item.id)) {
                return rowsMap.get(item.id) as WorksheetLineItem | ProposalSubCategory;
            }
            if (isProposalSubCategory(item)) {
                return getUpdatedSubCategory(item, rowsMap);
            }
            return item;
        }),
    };
};

const getUpdatedSubCategory = (
    subCategory: ProposalSubCategory,
    rowsMap: Map<string | number, WorksheetLineItemContainerRow>,
) => {
    return {
        ...subCategory,
        items: subCategory.items.map((subItem) => {
            if (rowsMap.has(subItem.id)) {
                return rowsMap.get(subItem.id) as WorksheetLineItem;
            }
            return subItem;
        }),
    };
};

export const generateUniqueCategoryTitle = (
    formatItems: WorksheetLineItemContainerRow[],
    category: ProposalCategory,
): string => {
    const flattenedList = flattenNestedFormatData(formatItems);
    if (!isProposalSubCategory(category)) {
        const siblingCategories = flattenedList.filter(
            (x) =>
                isProposalLineItemGroupRow(x) && !isProposalSubCategory(x) && x.id !== category.id,
        ) as ProposalCategory[];

        return renameDuplicateCategory(category.title, siblingCategories);
    } else {
        const siblingSubCategories = flattenedList.filter(
            (x) =>
                isProposalSubCategory(x) &&
                x.parentId === category.parentId &&
                x.id !== category.id,
        ) as ProposalCategory[];

        return renameDuplicateCategory(category.title, siblingSubCategories);
    }
};

export const maxCategoryTitleLength = 200;

export const renameDuplicateCategory = (
    title: string,
    siblingCategories: ProposalCategory[],
): string => {
    let index = 1;
    let currentTitle = title;
    const isDuplicateTitle = (x: ProposalCategory) =>
        x.title.toLowerCase().trim() === currentTitle.toLowerCase().trim();
    while (siblingCategories.some(isDuplicateTitle)) {
        const suffix = ` (${index})`;
        if (currentTitle.length + suffix.length > maxCategoryTitleLength) {
            currentTitle = currentTitle.slice(0, maxCategoryTitleLength - suffix.length);
        }
        currentTitle = `${title}${suffix}`;
        index++;
        if (index > 100) {
            throw new Error("Please select a different title for the group.");
        }
    }
    return currentTitle;
};

// Change Order line items should not have parent groups. This method removes the group from ChangeOrder line items so they display as a flat list.\
export const getDataForGrid = (
    formatItems: FormatItemList,
): FormatItemList | WorksheetLineItem[] => {
    if (
        // Only flatten to an ungrouped list when the sole group is the change order "unassigned"
        // group. Once real groups exist alongside it, keep the grouped structure so every group renders.
        formatItems.length === 1 &&
        isProposalLineItemGroupRow(formatItems[0]) &&
        formatItems[0].items.length > 0 &&
        formatItems[0].items.every(
            (item) => isProposalLineItemRow(item) && item.lineItemType === LineItemType.ChangeOrder,
        )
    ) {
        return formatItems[0].items;
    } else {
        return formatItems;
    }
};

export const canEditTitleOrCostCode = (
    lineItem: { id: number; lineItemType: LineItemType },
    isRevisedCosts: boolean,
) => {
    // Revised costs is always readonly.
    if (isRevisedCosts) {
        return false;
    }

    // Change order worksheet allows title and cost code editing inline.
    if (lineItem.lineItemType === LineItemType.ChangeOrder) {
        return true;
    }

    // New Items are always allowed to have title and cost code edited.
    return lineItem.id === 0;
};

export const applyDefaultMarginMarkupToNewLineItem = (
    costTypesMarkup: ICostTypesMarkup[],
    wli: WorksheetLineItem,
) => {
    const defaultMarkupMargin = getDefaultMarkupMarginForCostType(costTypesMarkup, wli.costTypes);
    let markup = defaultMarkupMargin.markupPercent;
    if (markup === 0 && defaultMarkupMargin.margin !== 0) {
        markup = getMarkupPercentageFromMarginPercentage(defaultMarkupMargin.margin ?? 0);
        wli.markupType = MarkupColumnTypes.Margin;
        wli.margin = defaultMarkupMargin.margin ?? 0;
    }
    wli.markupPercent = markup;
    return {
        ...wli,
        ...recalculateAllFields({ ...wli }, false),
    };
};
